import { ComponentPortal } from '@angular/cdk/portal';
import { ComponentRef, Type, Injector, ViewContainerRef } from '@angular/core';

import { GOOGLE_MAP_MARKER_DATA } from '../../providers';
import { BaseMarker } from './base-marker';
import { IBaseMarkerEvents } from './base-marker-events';
import { IGoogleMapComponentMarkerOptions } from './component-marker-options';

const DEFAULT_OPTIONS: IGoogleMapComponentMarkerOptions = {};

export class GoogleMapComponentMarker<
  TComponent = any,
  TEvents extends IBaseMarkerEvents = IBaseMarkerEvents,
> extends BaseMarker<TEvents> {
  private readonly _element: HTMLElement;

  private readonly _component: Type<TComponent>;
  private _componentRef: ComponentRef<TComponent>;

  private readonly _data: any;
  private readonly _viewContainerRef: ViewContainerRef;

  constructor(component: Type<TComponent>, options?: IGoogleMapComponentMarkerOptions) {
    super(options);

    this._component = component;

    options = { ...DEFAULT_OPTIONS, ...options };

    this._data = options.data;
    this._viewContainerRef = options.viewContainerRef;

    this._element = this._createElement();

    this._initInnerWatchers();
  }

  get data(): any {
    return this._data;
  }

  public getComponentInstance(): TComponent {
    return this._componentRef && this._componentRef.instance;
  }

  protected getElement(): HTMLElement {
    return this._element;
  }

  protected draw(): this {
    this._updatePosition();

    return this;
  }

  protected onAdd(): void {
    this._updateZIndex();
    this._updateOpacity();
    this._updatePosition();
    this._updateClickable();

    this._createComponent();

    this.getPanes().markerLayer.appendChild(this._element);
  }

  protected onRemove(): void {
    this._destroyMarkerComponent();
  }

  private _updatePosition(): void {
    if (!this.map) {
      return;
    }

    const element = this._element;

    const lonLat = this.getLonLat();
    const point = this.fromLonLatToPoint(lonLat);
    element.style.top = `${point.y}px`;
    element.style.left = `${point.x}px`;
  }

  private _updateZIndex(): void {
    const zIndex = this.getZIndex();
    this._element.style.zIndex = zIndex.toString();
  }

  private _updateOpacity(): void {
    const opacity = this.getOpacity();
    this._element.style.opacity = opacity.toString();
  }

  private _updateClickable(): void {
    const clickable = this.getClickable();
    this._element.style.cursor = clickable ? 'pointer' : 'inherit';
  }

  private _createComponent(): void {
    const parentInjector = this._viewContainerRef
      ? this._viewContainerRef.injector
      : this.map.injector;
    const viewContainerRef = this._viewContainerRef
      ? this._viewContainerRef
      : this.map.viewContainerRef;

    const componentInjector = Injector.create({
      providers: [{ provide: GOOGLE_MAP_MARKER_DATA, useValue: this._data }],
      parent: parentInjector,
    });

    const componentPortal = new ComponentPortal(
      this._component,
      viewContainerRef,
      componentInjector,
    );

    this._componentRef = this.map.portal.attachComponentPortal(componentPortal);

    this._element.appendChild(this._componentRef.location.nativeElement);
  }

  private _destroyMarkerComponent(): void {
    if (this._element.hasChildNodes()) {
      this._element.removeChild(this._componentRef.location.nativeElement);
    }

    this._componentRef.destroy();
    this._componentRef = null;
  }

  private _initInnerWatchers(): void {
    this.on('lonLatChange').subscribe(this._updatePosition.bind(this));
    this.on('zIndexChange').subscribe(this._updateZIndex.bind(this));
    this.on('opacityChange').subscribe(this._updateOpacity.bind(this));
    this.on('clickableChange').subscribe(this._updateClickable.bind(this));
  }

  private _createElement(): HTMLElement {
    const element = document.createElement('div');
    element.style.position = 'absolute';

    return element;
  }
}
